import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { BehaviorSubject, filter, firstValueFrom, take } from 'rxjs';
import moment from 'moment';
import { Logger } from '../../../infrastructure/logging/logger';
import { Country } from '../../../shared/address/Country';
import { State } from '../../../shared/address/State';
import { CountryService } from '../../../shared/reference/country.service';
import { ReferenceService } from '../../../shared/reference/reference.service';
import { getStateCode } from '../../../utilities/AddressUtility';
import { getCountryNameFromCountryId } from '../../../utilities/CountryUtility';
import { isStringEmptyOrNull } from '../../../utilities/StringUtility';
import { OrganizationService } from '../../organizations/services/organizations.service';
import { Reservation } from '../../reservations/models/Reservation';
import { ReservationService } from '../../reservations/services/reservation.service';
import { ClaimFileUploadErrorDialogComponent } from '../components/claim-file-upload-error-dialog/claim-file-upload-error-dialog.component';
import { NewClaimExitDialogComponent } from '../components/new-claim-exit-dialog/new-claim-exit-dialog.component';
import { NewClaimExitInvalidDialogComponent } from '../components/new-claim-exit-invalid-dialog/new-claim-exit-invalid-dialog.component';
import { NewClaimSaveDraftDialogComponent } from '../components/new-claim-save-draft-dialog/new-claim-save-draft-dialog.component';
import { Claim, ClaimDocument } from '../models/Claim';
import { ClaimsService } from '../services/claims.service';
import { SupportingDocumentSection, SupportingDocument } from './components/supporting-documents-step/models/SupportingDocumentSection.model';
import { ReservationDetailCardComponent } from '../../../shared/components/reservation-detail-card/reservation-detail-card.component';
import { WhatHappenedStepComponent } from './components/what-happened-step/what-happened-step.component';
import { PaymentInformationStepComponent } from './components/payment-information-step/payment-information-step.component';
import { SupportingDocumentsStepComponent } from './components/supporting-documents-step/supporting-documents-step.component';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ReferenceFileUploadResponse } from '../../../shared/files/ReferenceFileUploadResponse';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { AuthService } from '../../../infrastructure/config/msal-config/auth.service';

@Component({
  selector: 'app-claim-form',
  templateUrl: './claim-form.component.html',
  styleUrls: ['./claim-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinner,
    MatIcon,
    MatExpansionModule,
    MatButtonModule,
    MatProgressBarModule,
    BackButtonComponent,
    ReservationDetailCardComponent,
    WhatHappenedStepComponent,
    PaymentInformationStepComponent,
    SupportingDocumentsStepComponent,
  ]
})
export class ClaimFormComponent implements OnInit {

  LEGACY_BOOL_FIELDS = [
    'claim_cause_damage_rental_property',
    'claim_cause_damage_guest_property',
    'claim_cause_injury',
    'claim_cause_theft_fire',
    'claim_cause_other',
    'would_rent_again',
    'damage_accidental',
    'home_liveable'
  ];

  organizationId!: number;
  reservationId!: number;
  claimId?: string | null;
  existingDraft: boolean = false;
  reservation!: Reservation;
  organization!: any;
  sendCheckToSafely: boolean = false;
  claim!: Claim;
  countries!: Country[];
  states!: State[];

  saveDraftEnabled: boolean = true;
  submitClaimEnabled: boolean = false;
  injuryLossFormInvalid = false;

  whatHappenedForm!: FormGroup;
  paymentInformationForm!: FormGroup;
  claimContactForm!: FormGroup;
  governmentDepartmentForm!: FormGroup;
  injuryAddressForm!: FormGroup;
  lossForm!: FormGroup;

  supportingDocumentSections: SupportingDocumentSection[] = [];
  existingSupportingDocuments$: BehaviorSubject<SupportingDocument[]> = new BehaviorSubject<SupportingDocument[]>([]);
  invalidSupportingDocumentNames: string[] = [];
  claimDocumentsWaitingForUpload: any[] = [];
  totalClaimDocumentsWaitingForUpload = 0;

  injury_address_id: any = '';
  goverment_department_id: any = null;
  send_check_to_address_id: any = '';
  send_check_to_address: any;
  contextLoaded = false;
  loading = new BehaviorSubject<boolean>(false);
  disableSaving: boolean = false;
  createdDate?: string;

  constructor(
    private reservationService: ReservationService,
    private organizationService: OrganizationService,
    private claimService: ClaimsService,
    private referenceService: ReferenceService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private router: Router,
    private userContextProvider: UserContextProvider,
    private countryService: CountryService,
    private logService: Logger,
    private cdr: ChangeDetectorRef,
    private authService: AuthService
  ) { }

  ngOnInit() {
    this.resetState();

    this.userContextProvider.isContextLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(_ => {
      this.onContextLoaded();
    });
  }

  ngAfterViewChecked() {
    this.submitClaimEnabled = this.canCreateClaim();
    this.cdr.detectChanges();
  }

  private resetState() {
    this.claimDocumentsWaitingForUpload = [];
  }

  onContextLoaded() {
    this.contextLoaded = true;
    this.loading.next(true);
    try {
      this.reservationId = this.getReservationIdFromUrl();
      this.claimId = this.getClaimIdFromUrl();
      this.states = this.referenceService.getAllStates();
      this.initForms();

      this.countryService.getCountries().then((countries: Country[]) => {
        this.countries = countries;
      }).finally(() => {
        if (this.loadingComplete()) {
          this.loading.next(false);
        }
      });

      this.reservationService.getReservationById(this.reservationId).then((reservation: Reservation) => {
        this.reservation = reservation;
        if (this.claimId) {
          this.reservation.claim_id = Number(this.claimId);
        }
        this.organizationService.getOrganizationDetail(reservation.organization_id).then((organization: any) => {
          this.organization = organization;
          this.organizationId = organization.id;
          this.sendCheckToSafely = organization.send_check_to_safely == 1;
        }).finally(() => {
          if (this.loadingComplete()) {
            this.loading.next(false);
          }
        });
      }).finally(() => {
        if (this.loadingComplete()) {
          this.loading.next(false);
        }
      });

      if (this.claimId) {
        this.claimService.getClaimById(this.claimId).then((claim: Claim) => {
          this.loadFromClaim(claim);
          this.claim = claim;
          this.createdDate = claim.created_date;
          this.existingDraft = true;
        }).finally(() => {
          if (this.loadingComplete()) {
            this.loading.next(false);
          }
        });
      }
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Claim form', 'An error occurred while retrieving Claim form');
    }
  }

  loadingComplete() {
    let reservationLoaded: boolean = this.reservation !== undefined && this.reservation !== null;
    let organizationLoaded: boolean = this.organization !== undefined && this.organization !== null;
    let countriesLoaded: boolean = this.countries !== undefined && this.countries !== null && this.countries.length > 0;

    let claimLoaded: boolean = false;
    if (this.claimId === undefined || this.claimId === null) {
      claimLoaded = true;
    } else {
      claimLoaded = this.claim !== undefined || this.claim !== null;
    }

    return reservationLoaded && organizationLoaded && claimLoaded && countriesLoaded;
  }

  async loadFromClaim(claim: Claim) {
    this.whatHappenedForm.patchValue(claim);
    this.lossForm.patchValue(claim);

    if (!this.sendCheckToSafely) {
      this.paymentInformationForm.patchValue(claim);
      this.send_check_to_address_id = claim.send_check_to_address_id;
      this.send_check_to_address = claim.send_check_to_address;
      this.send_check_to_address.send_check_to_name = claim.send_check_to_name;
      this.send_check_to_address.send_check_to_phone = claim.send_check_to_phone;
      this.send_check_to_address.send_check_to_email = claim.send_check_to_email;
    }

    if (claim.injury_address_id && claim.claim_cause_injury != 0) {
      this.injury_address_id = claim.injury_address_id;
      this.whatHappenedForm.addControl('injury_address', this.injuryAddressForm);
      this.injuryAddressForm.patchValue(claim);

      if(claim.injury_address?.country_id == '1') {
        const state = this.states.find(state => state.label == claim.injury_address?.state || state.value == claim.injury_address?.state);
        this.injuryAddressForm.get('injury_address')?.patchValue({ state: state?.value });
      }
    }

    if (claim.goverment_department_id && claim.claim_cause_theft_fire != 0) {
      this.goverment_department_id = claim.goverment_department_id;
      this.whatHappenedForm.addControl('goverment_department', this.governmentDepartmentForm);
      this.governmentDepartmentForm.patchValue(claim.goverment_department!);
    }

    if(claim.send_check_to_address && claim.send_check_to_address.state) {
      const state = getStateCode(claim.send_check_to_address.state);
      this.paymentInformationForm.get('send_check_to_address')?.patchValue({ state: state });
    }

    this.whatHappenedForm.markAllAsTouched();
    this.whatHappenedForm.markAsDirty();

    this.paymentInformationForm.markAllAsTouched();
    this.paymentInformationForm.markAsDirty();

    this.loadSupportingDocuments(claim.claim_documents);

    this.cdr.detectChanges();
  }

  private loadSupportingDocuments(documents: ClaimDocument[]) {
    this.existingSupportingDocuments$.next(documents.map(document => {
      return {
        id: String(document.file_id),
        fileKey: '',
        fileName: document.file_name,
        fileSize: 0,
        fileData: null,
        fileType: '',
        description: document.description
      }
    }));
  }

  getReservationIdFromUrl() {
    return Number(this.route.snapshot.queryParamMap.get('reservationId'));
  }

  initForms() {
    this.initSubForms();
    this.whatHappenedForm = this.formBuilder.group({
      damage_date: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      damage_description: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      lossForm: this.lossForm,
      estimate_amount: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      home_liveable: ['', Validators.required],
      damage_accidental: ['', Validators.required],
      guest_rate: [null, Validators.required],
      would_rent_again: ['', Validators.required],
      admin_comment: [''],
      additional_comment: ['']
    });

    this.paymentInformationForm = this.formBuilder.group({
      send_check_to: ['', Validators.required],
      send_check_to_name: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      send_check_to_phone: ['', [Validators.required, Validators.pattern(/^[\s()+-]*([0-9][\s()+-]*){10,11}(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i)]],
      send_check_to_email: ['', [Validators.required, Validators.email]],
      send_check_to_address: this.formBuilder.group({
        address_line_1: ['', [Validators.required, Validators.pattern(/[\S]/)]],
        address_line_2: ['', Validators.pattern(/[\S]/)],
        city: ['', [Validators.required, Validators.pattern(/[\S]/)]],
        state: ['', [Validators.required, Validators.pattern(/[\S]/)]],
        postal_code: ['', [Validators.required, Validators.pattern(/[\S]/)]],
        country_id: [1, [Validators.required, Validators.pattern(/[\S]/)]],
        country_name: ['']
      })
    });

    this.claimContactForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^[\s()+-]*([0-9][\s()+-]*){10,11}(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i)]],
      email: ['', [Validators.required, Validators.email]],
    });
  }

  initSubForms() {
    this.injuryAddressForm = this.formBuilder.group({
      id: [''],
      injury_description: ['', [Validators.pattern(/[\S]/)]],
      injury_name: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      injury_phone: ['', [Validators.required, Validators.pattern(/^[\s()+-]*([0-9][\s()+-]*){10,11}(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i)]],
      injury_address: this.formBuilder.group({
        address_line_1: ['', [Validators.required, Validators.pattern(/[\S]/)]],
        address_line_2: ['', Validators.pattern(/[\S]/)],
        city: ['', [Validators.required, Validators.pattern(/[\S]/)]],
        state: ['', [Validators.required, Validators.pattern(/[\S]/)]],
        postal_code: ['', [Validators.required, Validators.pattern(/[\S]/)]],
        country_id: [1, [Validators.required, Validators.pattern(/[\S]/)]]
      })
    });

    this.governmentDepartmentForm = this.formBuilder.group({
      id: [null],
      name: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      case_number: ['', Validators.pattern(/[\S]/)],
      officer_name: ['', Validators.pattern(/[\S]/)],
      officer_phone: ['', Validators.pattern(/^[\s()+-]*([0-9][\s()+-]*){10,11}(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i)],
    });

    this.lossForm = this.formBuilder.group({
      claim_cause_damage_rental_property: [false],
      claim_cause_damage_guest_property: [false],
      claim_cause_injury: [false],
      claim_cause_theft_fire: [false],
      claim_cause_other: [false],
    }, { validators: this.requireCheckboxesToBeCheckedValidator() });
  }

  requireCheckboxesToBeCheckedValidator(): ValidatorFn {
    return function validate(formGroup: any) {
      let checked = 0;

      Object.keys(formGroup.controls).forEach(key => {
        const control = formGroup.controls[key];
        if (control.value == true) {
          checked++;
        }
      });

      if (checked < 1) {
        return {
          requireCheckboxesToBeChecked: true,
        };
      }
      return null;
    };
  }

  navigateToClaimDetailPage(claimId: string) {
    this.router.navigate([`/claims/detail/${claimId}`]);
  }

  getClaimIdFromUrl() {
    return this.route.snapshot.queryParamMap.get('claimId');
  }

  onBackClick(): void {
    if (!this.canSaveDraft()) {
      this.openInvalidDraftOnBackDialog();
    } else {
      this.openSaveDraftOnBackDialog();
    }
  }

  openSaveDraftOnBackDialog() {
    const dialogRef = this.dialog.open(NewClaimSaveDraftDialogComponent, {
      width: '500px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(async shouldSaveDraft => {
      if (shouldSaveDraft) {
        await this.submitClaim(false, false);
      }

      this.claimId ? this.navigateToClaimDetailPage(this.claimId) : this.navigateToReservationDetailPage(this.reservationId);
    });
  }

  openInvalidDraftOnBackDialog() {
    const dialogRef = this.dialog.open(NewClaimExitInvalidDialogComponent, {
      width: '500px',
      disableClose: true,
      data: { fields: this.getInvalidRequiredDraftFields() }
    });

    dialogRef.afterClosed().subscribe(async shouldExit => {
      if (shouldExit) {
        this.claimId ? this.navigateToClaimDetailPage(this.claimId) : this.navigateToReservationDetailPage(this.reservationId);
      }
    });
  }

  getInvalidRequiredDraftFields(): string[] {
    let fields = [];
    let whatHappenedData = { ...this.whatHappenedForm.value }
    if (isStringEmptyOrNull(whatHappenedData.damage_description)) {
      fields.push('When did the damage occur');
    }

    if (isStringEmptyOrNull(whatHappenedData.estimate_amount)) {
      fields.push('Total claim amount');
    }

    return fields;
  }

  navigateToReservationDetailPage(reservationId: number) {
    this.router.navigate([`/reservations/detail/${reservationId}`]);
  }

  canSaveDraft(): boolean {
    const damageDescription = this.whatHappenedForm?.get('damage_description')?.value;
    const estimateAmount = this.whatHappenedForm?.get('estimate_amount')?.value;
    const damageDateValid = this.whatHappenedForm?.get('damage_date')?.valid || false;
    const newSupportingDocumentsHaveDescriptions = this.supportingDocumentSections?.every(section => section.description?.length > 0 && section.documents?.length > 0);

    let injuryFormValid = true;
    if(this.whatHappenedForm.get('injury_address')) {
      injuryFormValid = this.injuryAddressForm.get('injury_address.country_id')?.value != null;
    }
    this.injuryLossFormInvalid = !injuryFormValid;

    return (
      damageDateValid &&
      !isStringEmptyOrNull(damageDescription) &&
      Number(estimateAmount) > 0 &&
      !isStringEmptyOrNull(estimateAmount) &&
      !this.disableSaving &&
      injuryFormValid &&
      newSupportingDocumentsHaveDescriptions
    );
  }

  canCreateClaim(): boolean {
    const paymentInfoValid = this.shouldShowPaymentInfoStep() ? this.paymentInformationForm?.valid : true;
    let injuryFormValid = true;
    if(this.whatHappenedForm?.get('injury_address')) {
      injuryFormValid = this.injuryAddressForm.get('injury_address.country_id')?.value != null;
    }
    this.injuryLossFormInvalid = !injuryFormValid;

    return (
      this.whatHappenedForm?.valid &&
      paymentInfoValid &&
      this.supportingDocumentsValid() &&
      !this.disableSaving &&
      injuryFormValid
    );
  }

  supportingDocumentsValid() {
    const allSectionsHaveDescriptions = this.supportingDocumentSections?.filter(section => section.documents.length > 0)
      .every(section => section.description?.length > 0);

    return allSectionsHaveDescriptions;
  }

  async submitClaim(submit: boolean, showExitPrompt = true) {
    this.disableSaving = true;

    try {
      this.loading.next(true);
      let claim = await this.buildClaim(submit);

      if (this.existingDraft) {
        this.claimId = (await this.claimService.updateClaimDraft(this.claimId, claim))?.id;
      } else {
        this.claimId = (await this.claimService.createClaim(claim))?.id;
        this.existingDraft = true;
      }

      if (!this.claimId) {
        this.logService.handleError('Claim ID does not exist', 'An error occurred while submitting Claim', 'Error while submitting Claim');
      }

      await this.uploadClaimDocuments(this.claimId, this.organizationId, this.supportingDocumentSections);

      if (submit) {
        this.handleSubmitClaimResponse(this.claimId);
      } else if(showExitPrompt) {
        this.handleSaveDraftResponse();
      }
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while submitting Claim', 'Error while submitting Claim');
    } finally {
      this.disableSaving = false;
      this.loading.next(false);
    }
  }

  private handleSubmitClaimResponse(claimId: string) {
    this.navigateToClaimDetailPage(claimId);
  }

  private handleSaveDraftResponse() {
    const dialogRef = this.dialog.open(NewClaimExitDialogComponent, { width: '500px', disableClose: true });

    dialogRef.afterClosed().subscribe(async shouldExit => {
      if (shouldExit && this.claimId) {
        this.navigateToClaimDetailPage(this.claimId);
      } else {
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';
        this.router.navigate(['/claims/form'], { queryParams: { reservationId: this.reservationId, claimId: this.claimId } });
      }
    });
  }

  private async uploadClaimDocuments(claimId: string, organizationId: number, sections: SupportingDocumentSection[]) {
    for(const section of sections) {
      this.prepareClaimDocumentSectionForUpload(claimId, organizationId, section);
    }
    this.populatePendingDocumentUploadCount(sections);

    const documents = sections.flatMap(section => section.documents);
    const uploadResults = await Promise.allSettled(documents.map(this.uploadClaimDocument.bind(this)));
    const successfulUploads: ReferenceFileUploadResponse[] = [];
    const failedUploads: ReferenceFileUploadResponse[] = [];
    uploadResults.forEach(uploadResult => {
      if (uploadResult.status == 'fulfilled') {
        if (uploadResult.value.error) {
          failedUploads.push(uploadResult.value);
        } else {
          successfulUploads.push(uploadResult.value);
        }
      } else {
        const failedUploadReason = uploadResult.reason as ReferenceFileUploadResponse;
        failedUploads.push(failedUploadReason);
      }
    });

    if (successfulUploads.length > 0) {
      this.claimService.updateClaimDocuments(claimId, organizationId, successfulUploads.map(uploadResult => {
        return this.mapUploadResponseToClaimDocument(uploadResult);
      }));
    }

    if (failedUploads.length > 0) {
      return await this.handleFailedClaimDocumentUploads(
        failedUploads.map(failedUpload => failedUpload.fileName)
      );
    }
  }

  private mapUploadResponseToClaimDocument(response: ReferenceFileUploadResponse): ClaimDocument {
    return {
      description: response.description ?? '',
      file_name: response.fileName,
      file_id: response.fileId
    };
  }

  private async uploadClaimDocument(document: SupportingDocument): Promise<ReferenceFileUploadResponse> {
      try {
        return await this.referenceService.uploadFile(
          this.organizationId.toString(),
          document.fileKey,
          document.fileName,
          document.fileType,
          document.fileSize,
          document.fileData,
          document.description
        );
      } finally {
        this.removeDocumentFromPendingUpload(document.fileKey);
      }
  }

  private async handleFailedClaimDocumentUploads(fileNames: string[]) {
    return await firstValueFrom(
      this.openFileUploadErrorDialog(fileNames).afterClosed()
    );
  }

  private populatePendingDocumentUploadCount(sections: SupportingDocumentSection[]) {
    this.claimDocumentsWaitingForUpload = [...sections
      .map(section => section.documents.map(document => document.fileKey))
      .flat()];
    this.totalClaimDocumentsWaitingForUpload = this.claimDocumentsWaitingForUpload.length;
  }

  private removeDocumentFromPendingUpload(fileKey: string) {
    this.claimDocumentsWaitingForUpload = [...this.claimDocumentsWaitingForUpload.filter(key => key != fileKey)];
  }

  private prepareClaimDocumentSectionForUpload(claimId: string, organizationId: number, section: SupportingDocumentSection) {
    for(const document of section.documents) {
      document.fileKey = `o${organizationId}_c${claimId}_${document.fileName}`;
      document.claimId = claimId;
      document.description = section.description;
    }
  }

  openFileUploadErrorDialog(fileNames: string[]) {
    return this.dialog.open(ClaimFileUploadErrorDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        fileNames: fileNames
      }
    });
  }

  convertToLegacyBool(value: boolean) {
    return value ? 1 : 0;
  }

  shouldShowPaymentInfoStep() {
    return !this.sendCheckToSafely;
  }

  private initializeClaim(submit: boolean) {
    return {
        id: this.existingDraft ? this.claimId : null,
        status_id: submit ? 2 : 1,
        reservation_id: this.reservationId,
        organization_id: this.reservation?.organization_id,
        property_id: this.reservation?.property?.property_id,
        currency_id: 1,
        is_active: 1,
        ...this.whatHappenedForm.value,
        ...this.paymentInformationForm.value,
        claim_documents: [],//await this.saveSupportingDocuments(submit)
    };
  }

  private addExistingDocumentsToClaim(claim: any) {
    if(!this.existingDraft || this.existingSupportingDocuments$.value.length === 0) {
      return;
    }

    const existingClaimDocs = this.existingSupportingDocuments$.value.map((document) => {
      return {
        description: document.description ?? '',
        file_name: document.fileName,
        file_id: Number(document.id),
        claim_id: Number(this.claimId)
      }
    });

    claim.claim_documents.push(...existingClaimDocs);
  }

  private convertLegacyBoolFields(claim: any) {
    this.LEGACY_BOOL_FIELDS.forEach(field => {
      claim[field] = this.convertToLegacyBool(claim[field]);
    });
  }

  private setClaimDamageDate(claim: any) {
    if (claim.damage_date) {
      claim.damage_date = moment(claim.damage_date).utc().format('YYYY-MM-DD');
    }
  }

  private setClaimSendCheckToCountryName(claim: any) {
    if (claim.send_check_to_address?.country_id) {
      claim.send_check_to_address.country_name =
        getCountryNameFromCountryId(this.countries, claim.send_check_to_address.country_id);
    }
  }

  private setClaimGovernmentDepartment(claim: any) {
    if (this.whatHappenedForm.get('goverment_department')) {
      claim.goverment_department_id = this.goverment_department_id;
      claim.goverment_department = { ...this.whatHappenedForm.get('goverment_department')!.value };
    }
  }

  private setClaimSendCheckToSafely(claim: any) {
    if (this.sendCheckToSafely) {
      claim.send_check_to = 1;
    }
  }

  private setClaimInjury(claim: any) {
    if (this.whatHappenedForm.get('injury_address')) {
      const injury_data = { ...this.whatHappenedForm.get('injury_address')!.value };
      claim.injury_address_id = this.injury_address_id;
      claim.injury_name = injury_data.injury_name;
      claim.injury_phone = injury_data.injury_phone;
      claim.injury_description = injury_data.injury_description;
      claim.injury_address = injury_data.injury_address;
      claim.injury_address.country_name = getCountryNameFromCountryId(this.countries, claim.injury_address.country_id);
    }
  }

  private cleanupClaim(claim: any) {
    if (!this.existingDraft) {
      delete claim.injury_address_id;
      delete claim.goverment_department_id;
      claim.created_date = new Date();
      claim.updated_date = new Date();
    }
  }

  private setClaimCause(claim: any) {
    claim.claim_cause_damage_rental_property = this.lossForm.get('claim_cause_damage_rental_property')?.value;
    claim.claim_cause_damage_guest_property = this.lossForm.get('claim_cause_damage_guest_property')?.value;
    claim.claim_cause_injury = this.lossForm.get('claim_cause_injury')?.value;
    claim.claim_cause_theft_fire = this.lossForm.get('claim_cause_theft_fire')?.value;
    claim.claim_cause_other = this.lossForm.get('claim_cause_other')?.value;
    delete claim.lossForm;
  }

  async buildClaim(submit: boolean) {
    try {
      let claim = this.initializeClaim(submit);
      claim.account_id = this.authService.portalAccount.value?.account_id;
      this.addExistingDocumentsToClaim(claim);
      this.convertLegacyBoolFields(claim);
      this.setClaimDamageDate(claim);
      this.setClaimSendCheckToCountryName(claim);
      this.setClaimGovernmentDepartment(claim);
      this.setClaimSendCheckToSafely(claim);
      this.setClaimInjury(claim);
      this.cleanupClaim(claim);
      this.setClaimCause(claim);

      return claim;
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while submitting Claim', 'An error occurred while submitting Claim');
      return null;
    }
  }
}